import React, { useState } from 'react';
import AppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import IconButton from '@mui/material/IconButton';
import Button from '@mui/material/Button';
import DashboardOutlined from '@mui/icons-material/DashboardOutlined';
import Box from '@mui/material/Box';
import UserMenu from '@/components/nav-bar/user-menu';
import Paper from '@mui/material/Paper';
import { useUser, SignedIn, SignedOut } from '@clerk/clerk-react';
import ThemeSwitch from '@/components/common/theme-switch';
import '@fontsource/orbitron/index.css';
import CreateWizardModal from '../create-wizard/create-wizard.modal';
import { Avatar, Tooltip } from '@mui/material';
import { useTheme } from '@mui/material';
import Modal from '@mui/material/Modal';
import ActiveOrganization from './active-organization';
import MenuIcon from '@mui/icons-material/Menu';
import britestageLogo from '@/assets/britestage-logo.png';
import { Link, useLocation } from 'react-router';
import { useTranslations } from '@/i18n/use-translations';
import { useBreakpointDown } from '@/hooks/use-breakpoint';
import { useFeatureAccess } from '@/hooks/use-feature-access';
import { useActiveOrganizationStore } from '@/state/stores/use-organization-store';

const NavBar: React.FC = () => {
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [isOrgMenuOpen, setIsOrgMenuOpen] = useState(false);
  const location = useLocation();

  const theme = useTheme();
  const isSmallScreen = useBreakpointDown('sm');
  const isMediumScreen = useBreakpointDown('md');
  const isDarkMode = theme.palette.mode === 'dark';
  const t = useTranslations();
  const isDashboardRoute = location.pathname === '/contributions' || location.pathname.startsWith('/contributions/');
  const { hasAccess: hasCreateAccess } = useFeatureAccess('create');
  const { activeOrganization } = useActiveOrganizationStore();
  const hasActiveOrganization = !!activeOrganization;

  const { user } = useUser();

  const handleOrgMenuToggle = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsOrgMenuOpen(!isOrgMenuOpen);
  };

  const handleUserMenuToggle = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsUserMenuOpen(!isUserMenuOpen);
  };

  const handleCloseMenus = () => {
    setIsUserMenuOpen(false);
    setIsOrgMenuOpen(false);
  };

  const logo = (
    <Link to={hasActiveOrganization ? '/' : '/contributions'} style={{ textDecoration: 'none' }}>
      <img
        src={britestageLogo}
        alt="Britestage Logo"
        style={{
          width: '172px',
          height: '40.89px',
          objectFit: 'contain',
          cursor: 'pointer',
          display: 'block',
        }}
      />
    </Link>
  );

  const wizardOrEmpty = hasCreateAccess ? <CreateWizardModal /> : null;

  return (
    <>
      <Paper
        elevation={3}
        sx={{
          position: 'static',
          width: '100%',
          borderRadius: 0,
        }}
      >
        <AppBar
          position="fixed"
          sx={{
            left: 0,
            top: 0,
            boxShadow: 'none',
            zIndex: theme.zIndex.drawer + 1,
          }}
          onClick={handleCloseMenus}
        >
          <Toolbar
            sx={{
              px: { xs: 1, sm: 1, md: 3 },
              minHeight: { xs: '50px', md: '64px' },
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              backgroundColor: theme.palette.background.topbar,
              position: 'relative',
            }}
          >
            {/* Left section - Logo and Menu */}
            <Box sx={{ display: 'flex', alignItems: 'center', height: { xs: '50px', md: '64px' } }}>
              <SignedIn>
                {(isMediumScreen || isSmallScreen) && (
                  <IconButton
                    edge="end"
                    color="inherit"
                    onClick={handleOrgMenuToggle}
                    sx={{
                      width: '32px',
                      height: '32px',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mr: isSmallScreen ? 1 : 2,
                      transition: 'background-color 0.2s ease',
                    }}
                  >
                    <MenuIcon
                      fontSize="large"
                      sx={{
                        'borderRadius': '4px',
                        'padding': '4px',
                        'ml': 0.5,
                        'color': isDarkMode ? 'inherit' : '#0000008F',
                        '&:hover': {
                          backgroundColor: theme.palette.action.hover,
                        },
                      }}
                    />
                  </IconButton>
                )}
                {!isMediumScreen && logo}
              </SignedIn>

              <SignedOut>{isMediumScreen && logo}</SignedOut>
            </Box>

            {/* Center section - Mobile actions or Desktop Create Wizard - Absolutely centered */}
            <SignedIn>
              <Box
                sx={{
                  position: 'absolute',
                  left: '50%',
                  transform: 'translateX(-50%)',
                  display: 'flex',
                  alignItems: 'center',
                  gap: { xs: 1, md: 2 },
                }}
              >
                {isMediumScreen ? (
                  <>
                    <Tooltip title={t('common.dashboard')}>
                      <IconButton
                        component={Link}
                        to="/contributions"
                        sx={{
                          'color': isDashboardRoute ? 'primary.main' : 'action.active',
                          'border': '1px solid',
                          'borderColor': isDashboardRoute ? 'primary.main' : 'action.active',
                          'borderRadius': 1,
                          '&:hover': {
                            backgroundColor: 'action.hover',
                            color: 'primary.main',
                          },
                        }}
                      >
                        <DashboardOutlined />
                      </IconButton>
                    </Tooltip>
                    {wizardOrEmpty}
                  </>
                ) : (
                  <>
                    <Tooltip title={t('common.dashboard')}>
                      <Button
                        component={Link}
                        to="/contributions"
                        variant="outlined"
                        color={isDashboardRoute ? 'primary' : undefined}
                        sx={
                          isDashboardRoute
                            ? {
                                'borderColor': 'primary.main',
                                'color': 'primary.main',
                                '&:hover': {
                                  borderColor: 'primary.main',
                                  color: 'primary.main',
                                  backgroundColor: 'action.hover',
                                },
                              }
                            : {
                                'color': 'action.active',
                                'borderColor': 'action.active',
                                '&:hover': {
                                  borderColor: 'action.active',
                                  backgroundColor: 'action.hover',
                                  color: 'action.active',
                                },
                              }
                        }
                      >
                        {t('common.dashboard')}
                        <DashboardOutlined sx={{ ml: 0.5 }} />
                      </Button>
                    </Tooltip>
                    {wizardOrEmpty}
                  </>
                )}
              </Box>
            </SignedIn>

            {/* Right section - Theme switch and User menu */}
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <SignedIn>
                <ThemeSwitch />
                <IconButton
                  onClick={handleUserMenuToggle}
                  data-testid="user-menu-button"
                  sx={{
                    p: 0,
                    width: 48,
                    height: 48,
                    borderRadius: '50%',
                    overflow: 'hidden',
                  }}
                >
                  <Avatar src={user?.imageUrl} alt="User" sx={{ width: 32, height: 32, cursor: 'pointer' }} />
                </IconButton>
              </SignedIn>
            </Box>
          </Toolbar>
        </AppBar>
      </Paper>

      <Modal
        open={isUserMenuOpen}
        onClose={handleCloseMenus}
        aria-labelledby="user-menu-modal"
        aria-describedby="user-menu-modal-description"
      >
        <UserMenu open={isUserMenuOpen} onClose={handleCloseMenus} />
      </Modal>

      <Modal
        open={isOrgMenuOpen}
        onClose={handleCloseMenus}
        aria-labelledby="org-menu-modal"
        aria-describedby="org-menu-modal-description"
      >
        <ActiveOrganization isMobileMenuOpen={isOrgMenuOpen} onClose={handleCloseMenus} />
      </Modal>
    </>
  );
};

export default NavBar;
